import { z } from 'zod';
import { isoDate } from './fields.ts';
import { roleSchema, type Role } from './records.ts';

/** REST contracts for platform endpoints (auth, families, notifications, exports). */

const COMMON_PASSWORDS = new Set([
  'password123',
  'password1234',
  '1234567890',
  '12345678910',
  'qwertyuiop',
  'iloveyou123',
  'letmein1234',
  'welcome1234',
  'abc1234567',
  'football123',
  'baseball123',
  'sunshine123',
  'princess123',
  'passw0rd123',
  'qwerty12345',
  'family1234',
]);

export const passwordSchema = z
  .string()
  .min(10, 'Use at least 10 characters')
  .max(256, 'That password is too long')
  .refine((p) => !COMMON_PASSWORDS.has(p.toLowerCase()), 'That password is too common — try a short phrase');

export const emailSchema = z.string().trim().toLowerCase().pipe(z.email('Enter a valid email address').max(254));

export const displayNameSchema = z.string().trim().min(1, 'Required').max(60);

export const MEMBER_COLORS = ['teal', 'coral', 'violet', 'amber', 'sky', 'rose', 'green', 'indigo', 'orange', 'slate'] as const;
export type MemberColor = (typeof MEMBER_COLORS)[number];
export const memberColorSchema = z.enum(MEMBER_COLORS);

export const signupRequest = z.object({
  email: emailSchema,
  password: passwordSchema,
  displayName: displayNameSchema,
});

export const signinRequest = z.object({
  email: emailSchema,
  password: z.string().min(1).max(256),
});

export const forgotPasswordRequest = z.object({ email: emailSchema });

export const resetPasswordRequest = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
});

export const changePasswordRequest = z.object({
  currentPassword: z.string().min(1).max(256),
  newPassword: passwordSchema,
});

/** Device setup codes are 8 characters, shown as XXXX-XXXX. */
export const deviceCodeRequest = z.object({
  code: z
    .string()
    .transform((s) => s.replace(/[\s-]/g, '').toUpperCase())
    .pipe(z.string().regex(/^[A-Z2-9]{8}$/, 'Enter the 8-character code')),
});

export const deleteAccountRequest = z.object({
  password: z.string().max(256).optional(),
  confirm: z.literal('DELETE'),
});

export const updateProfileRequest = z.object({
  displayName: displayNameSchema.optional(),
  birthday: isoDate.nullable().optional(),
});

const appearanceFields = {
  mode: z.enum(['system', 'light', 'dark']),
  /** null = use the family default */
  theme: z.string().max(40).nullable(),
  accent: z.string().max(40).nullable(),
  background: z.string().max(40).nullable(),
  reduceMotion: z.enum(['system', 'reduce', 'allow']),
  textSize: z.enum(['normal', 'large']),
};

/** Personal appearance, saved with the account so it follows the person to every device. */
export const appearancePreferencesSchema = z.object({
  mode: appearanceFields.mode.default('system'),
  theme: appearanceFields.theme.default(null),
  accent: appearanceFields.accent.default(null),
  background: appearanceFields.background.default(null),
  reduceMotion: appearanceFields.reduceMotion.default('system'),
  textSize: appearanceFields.textSize.default('normal'),
});
export type AppearancePreferences = z.infer<typeof appearancePreferencesSchema>;

export const userPreferencesSchema = z.object({
  appearance: appearancePreferencesSchema.prefault({}),
  /** ISO time of the last change, so devices can tell which copy is newer. */
  updatedAt: z.string().nullable().default(null),
});
export type UserPreferences = z.infer<typeof userPreferencesSchema>;

/** Only the fields being changed; no defaults here, so a partial update never resets other choices. */
export const updatePreferencesRequest = z.object({
  appearance: z.object(appearanceFields).partial(),
});

const familySettingsFields = {
  weekStartsOn: z.union([z.literal(0), z.literal(1), z.literal(6)]),
  currency: z.string().regex(/^[A-Z]{3}$/, 'Use a 3-letter currency code like USD'),
  timezone: z.string().max(64),
  /** Family-wide default appearance; each person can override it. */
  theme: z.string().max(40).nullable(),
  accent: z.string().max(40).nullable(),
  background: z.string().max(40).nullable(),
};

export const familySettingsSchema = z.object({
  weekStartsOn: familySettingsFields.weekStartsOn.default(0),
  currency: familySettingsFields.currency.default('USD'),
  timezone: familySettingsFields.timezone.default('UTC'),
  theme: familySettingsFields.theme.default(null),
  accent: familySettingsFields.accent.default(null),
  background: familySettingsFields.background.default(null),
});
export type FamilySettings = z.infer<typeof familySettingsSchema>;

export const createFamilyRequest = z.object({
  name: z.string().trim().min(1, 'Required').max(80),
  timezone: z.string().max(64).optional(),
  color: memberColorSchema.optional(),
});

export const updateFamilyRequest = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  // No defaults in an update: changing one setting must not reset the others (zod's partial() would fill defaults).
  settings: z.object(familySettingsFields).partial().optional(),
});

export const deleteFamilyRequest = z.object({
  confirmName: z.string().min(1).max(80),
});

export const createInviteRequest = z.object({
  email: emailSchema.nullable().optional(),
  role: z.enum(['manager', 'adult', 'child']).default('adult'),
});

/** Invitation codes use the same unambiguous alphabet as device codes (no 0/O, 1/I/L), shown as XXXX-XXXX. */
export const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const CODE_RE = new RegExp(`^[${CODE_ALPHABET}]{8}$`);

/** Accepts "abcd-efgh", "ABCD EFGH" or "ABCDEFGH"; returns the canonical 8 characters, or null if it isn't a code. */
export function normalizeInviteCode(input: string): string | null {
  const s = input.replace(/[\s-]/g, '').toUpperCase();
  return CODE_RE.test(s) ? s : null;
}

export const formatCode = (code: string) => `${code.slice(0, 4)}-${code.slice(4)}`;

/**
 * Pull an invitation out of whatever someone pasted or typed: a full link
 * (any host — the link may have been made on another device), a bare token,
 * or a short code.
 */
export function parseInvitationInput(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const fromLink = trimmed.match(/\/join\/([^/?#\s]+)/)?.[1];
  if (fromLink) return decodeURIComponent(fromLink);
  const code = normalizeInviteCode(trimmed);
  if (code) return code;
  return /^[A-Za-z0-9_-]{20,200}$/.test(trimmed) ? trimmed : null;
}

export interface CreatedInvite {
  id: string;
  /** Long single-use token for the link; never stored by the server (only its hash). */
  token: string;
  /** Short code for typing on another device (XXXX-XXXX); same invitation as the token. */
  code: string;
  /** Link built from PUBLIC_ORIGIN, as used in emails. Clients on another origin build their own from the token. */
  link: string;
  expiresAt: string;
  emailed: boolean;
  /** How the email went out, when one was requested and handed off successfully. */
  delivery: MailDelivery | null;
  /** Set when an email was requested but the mail server refused it. The link and code still work. */
  emailError?: string;
}

/** smtp: handed to the configured mail server. development-outbox: written to a file on the server, not delivered. */
export type MailDelivery = 'smtp' | 'development-outbox';

/** What the server can actually do, so the UI never implies a service works when it isn't set up. */
export interface ServerCapabilities {
  email: MailDelivery;
  webPush: { configured: boolean; publicKey: string | null };
  google: { configured: boolean };
  sampleImports: boolean;
}

export const addChildRequest = z.object({
  displayName: displayNameSchema,
  birthday: isoDate.nullable().optional(),
  color: memberColorSchema,
});

export const updateMemberRequest = z.object({
  role: roleSchema.optional(),
  color: memberColorSchema.optional(),
  displayName: displayNameSchema.optional(),
  birthday: isoDate.nullable().optional(),
});

export interface UserProfile {
  id: string;
  email: string | null;
  displayName: string;
  birthday: string | null;
  isManagedChild: boolean;
  hasPassword: boolean;
  createdAt: string;
}

export interface FamilySummary {
  id: string;
  name: string;
  role: Role;
  color: MemberColor;
  settings: FamilySettings;
  accessEpoch: number;
}

export interface MeResponse {
  user: UserProfile;
  families: FamilySummary[];
  preferences: UserPreferences;
  serverTime: number;
  features: { devMode: boolean; disabledModules: string[] };
}

export interface FamilyMember {
  userId: string;
  displayName: string;
  role: Role;
  color: MemberColor;
  birthday: string | null;
  isManagedChild: boolean;
  joinedAt: string;
}

export interface FamilyInvite {
  id: string;
  email: string | null;
  role: Role;
  createdAt: string;
  expiresAt: string;
  createdBy: string;
}

export interface FamilyDetail {
  id: string;
  name: string;
  settings: FamilySettings;
  createdAt: string;
  members: FamilyMember[];
  /** Managers only. */
  invites: FamilyInvite[];
  storage: FamilyStorage;
}

/** Plans exist so storage isn't assumed to be infinite; there is no billing in this build. */
export const PLAN_IDS = ['free', 'plus'] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export interface FamilyStorage {
  plan: PlanId;
  usedBytes: number;
  limitBytes: number;
}

export interface InvitePreview {
  familyName: string;
  invitedBy: string;
  role: Role;
  email: string | null;
  expiresAt: string;
}

export const NOTIFICATION_CATEGORIES = ['reminders', 'assignments', 'calendar', 'comments', 'family', 'updates'] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const notificationPreferencesSchema = z.record(z.enum(NOTIFICATION_CATEGORIES), z.object({ inApp: z.boolean(), push: z.boolean() }));
export type NotificationPreferences = Record<NotificationCategory, { inApp: boolean; push: boolean }>;

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  reminders: { inApp: true, push: true },
  assignments: { inApp: true, push: true },
  calendar: { inApp: true, push: false },
  comments: { inApp: true, push: true },
  family: { inApp: true, push: true },
  updates: { inApp: true, push: false },
};

export interface AppNotification {
  id: string;
  familyId: string | null;
  category: NotificationCategory;
  title: string;
  body: string;
  /** In-app route to open, e.g. "/calendar/event/<id>". */
  link: string | null;
  createdAt: string;
  readAt: string | null;
}

export const pushSubscriptionRequest = z.object({
  endpoint: z.url().max(2000),
  keys: z.object({ p256dh: z.string().max(200), auth: z.string().max(100) }),
  userAgent: z.string().max(300).optional(),
});

export const createExportRequest = z.object({
  familyId: z.uuid(),
  /** mine: only what I created/own. accessible: everything I can see. */
  scope: z.enum(['mine', 'accessible']).default('mine'),
});

export interface ExportJob {
  id: string;
  familyId: string;
  scope: 'mine' | 'accessible';
  status: 'pending' | 'running' | 'ready' | 'failed' | 'expired';
  createdAt: string;
  completedAt: string | null;
  expiresAt: string | null;
  sizeBytes: number | null;
  error: string | null;
}
